/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { inject, Inject, Injectable } from '@angular/core'
import { CanActivateFn } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import { Subject } from 'rxjs'
import { BootstrapData, ElementsGroup, ElementsUser, ElementsUserDetail } from 'elements-sdk'

import { APIService, BOOTSTRAP_DATA, HttpInterceptorService } from 'api/module'
import { PermissionSchema } from '../permissionModel'
import { LicenseService } from 'common/util/services/license.service'
import { SocketService } from 'common/util/services/socket.service'
import { MessageBoxService } from 'common/util/services/messageBox.service'

const isUser = (object: ElementsUser|ElementsGroup): object is ElementsUser => {
  return 'username' in object
}

export interface KnownPage {
  id: string
  permission: string
  component?: string
  name: string
  url: string
  icon: string
}

export interface KnownPageGroup {
  id?: string
  name: string|null
  component?: string
  pages: KnownPage[]
}

@Injectable({ providedIn: 'root' })
export class IdentityService {
  identityUpdated$ = new Subject<void>()
  loggedIn = false
  user: ElementsUserDetail|null = null
  knownPages: KnownPageGroup[]
  permissionSchema: PermissionSchema = { permissionGroups: [] }

  constructor (
    @Inject(BOOTSTRAP_DATA) private bootstrapData: BootstrapData,
    private license: LicenseService,
    private translate: TranslateService,
    private api: APIService,
    private messageBox: MessageBoxService,
    private interceptor: HttpInterceptorService,
    socket: SocketService,
  ) {
    this.initConstants()

    socket.subject('user:updated').subscribe(data => {
      if (data.id === this.user?.id) {
        this.reload()
      }
    })

    socket.subject('volumes:updated').subscribe(() => this.reload())

    if (bootstrapData.identityValue) {
      this.user = bootstrapData.identityValue
      this.loggedIn = true
    }
  }

  async reload (): Promise<void> {
    if (!this.user) {
      return
    }
    this.user = await this.api.main.getUser({
      id: this.user.id,
      includeAllowedFsPaths: true,
      includeEffectivePermissions: true,
    })
    this.identityUpdated$.next()
  }

  hasPermission (permission: any): boolean {
    if (!this.user?.effectivePermissions) {
      return false
    }
    if (this.user.username === 'root') {
      return true
    }
    return this.user.effectivePermissions.includes(permission)
  }

  hasFSAccess (path: string, mode = 'r'): boolean {
    const paths = mode === 'r' ? this.user?.allowedFsPaths : this.user?.allowedFsWritePaths
    return paths?.some(p => {
      if (p.endsWith('/')) {
        p = p.slice(0, -1)
      }
      if (path === p) {
        return true
      }
      return path.startsWith(p + '/')
    }) ?? false
  }

  async authorizeRoute (permission: any, component: any) {
    if (!this.hasPermission(permission)) {
      throw new Error('Route access denied')
    }
    if (component !== undefined && !this.license.hasComponent(component)) {
      throw new Error('Route access denied')
    }
  }

  async impersonate (user: ElementsUser): Promise<void> {
    if (!this.user) {
      return
    }
    window.open(`/api/2/auth/impersonation/redirect/${user.id}`)
  }

  async stopImpersonation (): Promise<void> {
    await this.api.auth.stopImpersonation()
    location.href = '/'
  }

  hasPagePermission (entity: ElementsUser|ElementsGroup, page: any): boolean {
    const isRoot = isUser(entity) && entity.username === 'root'
    return (!page.component || this.license.hasComponent(page.component)) &&
      (!page.permission || entity.effectivePermissions.includes(page.permission) || isRoot)
  }

  hasPagePermissionLegacy (entity: any, page: any): boolean {
    return (!page.component || this.license.hasComponent(page.component)) &&
      (!page.permission || entity?.effective_permissions.includes(page.permission) || entity?.username === 'root')
  }

  getPageFullName (page: KnownPage): string {
    const group = this.knownPages.find(g => g.pages.includes(page))!
    if (!group.name) {
      return page.name
    }
    return `${group.name}: ${page.name}`
  }

  getAvailablePages (entity: ElementsUser|ElementsGroup): KnownPage[] {
    let pages: KnownPage[] = []
    this.knownPages.forEach(category => {
      pages = pages.concat(category.pages
        .filter(page => this.hasPagePermission(entity, page)))
    })
    return pages
  }

  getAvailablePagesLegacy (entity: any): KnownPage[] {
    let pages: KnownPage[] = []
    this.knownPages.forEach(category => {
      pages = pages.concat(category.pages
        .filter(page => this.hasPagePermissionLegacy(entity, page)))
    })
    return pages
  }

  async logout (): Promise<void> {
    if (this.bootstrapData.activeSamlProvider) {
      const result = await this.messageBox.show({
        text: this.translate.instant('You\'re logged in via single sign-on. How do you want to log out?'),
        positive: this.translate.instant('Log out everywhere'),
        negative: this.translate.instant('Log out from ELEMENTS only'),
      }).then(() => true, () => false)
      if (result) {
        this.interceptor.enableSessionExpiryDetection = false
        window.location.assign(`/api/2/auth/saml/${this.bootstrapData.activeSamlProvider.id}/sls`)
        return
      }
    }
    this.interceptor.enableSessionExpiryDetection = false
    await this.api.auth.logout()
    window.location.assign('/')
  }

  isLoggedInViaLink (): boolean {
    return !!new URL(location.href).searchParams.get('session-key')
  }

  patchURLWithSessionKey (url: string, key?: string): string {
    key ??= this.bootstrapData.sessionId
    if (new URL(location.href).searchParams.get('session-key') && key) {
      const newURL = new URL(url, location.href)
      if (newURL.origin === location.origin) {
        newURL.searchParams.set('session-key', key)
        url = newURL.toString()
      }
    }
    return url
  }

  private initConstants () {
    this.knownPages = [
      {
        name: null,
        pages: [
          {
            id: 'status',
            permission: 'system:status:view',
            name: this.translate.instant('Status'),
            url: '/status',
            icon: 'chart-area',
          },
          {
            id: 'productions',
            permission: 'projects:view',
            name: this.translate.instant('Productions'),
            url: '/production',
            icon: 'briefcase',
            component: 'projects',
          },
          {
            id: 'workspaces',
            permission: 'projects:view',
            name: this.translate.instant('Workspaces'),
            url: '/workspace',
            icon: 'folder-open',
            component: 'projects',
          },
          {
            id: 'folder_templates',
            permission: 'folder_templates:manage',
            name: this.translate.instant('Folder Templates'),
            url: '/workspace-folder-templates',
            icon: 'clone',
            component: 'folder_templates',
          },
          {
            id: 'users',
            permission: 'users:view',
            name: this.translate.instant('Users'),
            url: '/user',
            icon: 'users',
          },
          {
            id: 'workstations',
            permission: 'workstations:view',
            name: this.translate.instant('Workstations'),
            url: '/workstation',
            icon: 'network-wired',
          },
          {
            id: 'shares',
            permission: 'shares:view',
            name: this.translate.instant('Shares'),
            url: '/share',
            icon: 'share-alt',
            component: 'storage',
          },
          {
            id: 'file-manager',
            permission: 'fs:fm',
            name: this.translate.instant('File Manager'),
            url: '/file-manager',
            icon: 'folders',
            component: 'storage',
          },
          {
            id: 'downloads',
            permission: 'downloads:view',
            name: this.translate.instant('Downloads'),
            url: '/download',
            icon: 'download',
          },
          {
            id: 'maintenance',
            permission: 'system:admin-access',
            name: this.translate.instant('System'),
            url: '/system',
            icon: 'wrench',
          },
        ],
      },
      {
        name: '',
        component: 'automation',
        pages: [
          {
            id: 'tasks',
            permission: 'tasks:view',
            name: this.translate.instant('Automation'),
            url: '/tasks',
            icon: 'cogs',
          },
        ],
      },
      {
        id: 'media-library',
        name: '',
        component: 'media',
        pages: [
          {
            id: 'media-home',
            permission: 'media:access',
            name: this.translate.instant('Media Library'),
            url: '/media',
            icon: 'film',
          },
        ],
      },
    ]
    if (this.bootstrapData.aiConnections.length) {
      this.knownPages.push({
        id: 'ai',
        name: '',
        pages: [
          {
            id: 'ai-home',
            permission: 'media:roots:manage',
            name: this.translate.instant('AI'),
            url: '/ai',
            icon: 'lightbulb-on',
          },
        ],
      })
    }
    this.knownPages.push({
      id: 'ltfs',
      name: this.translate.instant('Tape Library'),
      component: 'ltfs',
      pages: [
        {
          id: 'ltfs-search',
          permission: 'ltfs:search',
          name: this.translate.instant('Search'),
          url: '/archive/search',
          icon: 'search',
        },
        {
          id: 'ltfs-backup',
          permission: 'ltfs:backup',
          name: this.translate.instant('Archive'),
          url: '/archive/start',
          icon: 'download',
        },
        {
          id: 'ltfs-restore',
          permission: 'ltfs:restore',
          name: this.translate.instant('Restore'),
          url: '/archive/restore',
          icon: 'history',
        },
        {
          id: 'ltfs-jobs',
          permission: 'ltfs:manage',
          name: this.translate.instant('Jobs'),
          url: '/archive/jobs',
          icon: 'cog',
        },
        {
          id: 'ltfs-tapegroups',
          permission: 'ltfs:tapegroups:manage',
          name: this.translate.instant('Tape Groups'),
          url: '/archive/tapes',
          icon: 'th-large',
        },
        {
          id: 'ltfs-library',
          permission: 'ltfs:manage',
          name: this.translate.instant('Tape Library'),
          url: '/archive/library',
          icon: 'tape',
        },
      ],
    })
    this.knownPages.push({
      name: '',
      pages: [
        {
          id: 'about',
          permission: 'system:admin-access',
          name: this.translate.instant('About'),
          url: '/about',
          icon: 'info-circle',
        },
      ],
    })

    this.permissionSchema.permissionGroups = [
      {
        name: this.translate.instant('Client app'),
        icon: 'network-wired',
        items: [
          {
            id: 'client:access',
            name: this.translate.instant('Access'),
          },
          {
            id: 'client:tasks:start',
            name: this.translate.instant('Start jobs'),
            require: ['client:access'],
          },
        ],
      },
      {
        name: this.translate.instant('FTP'),
        icon: 'folder',
        component: 'storage',
        items: [
          {
            id: 'ftp:access',
            name: this.translate.instant('Access'),
          },
        ],
      },
      {
        name: this.translate.instant('Status'),
        icon: 'chart-area',
        items: [
          {
            id: 'system:status:view',
            name: this.translate.instant('View'),
          },
        ],
      },
      {
        name: this.translate.instant('Productions'),
        icon: 'briefcase',
        component: 'projects',
        items: [
          {
            id: 'projects:view',
            name: this.translate.instant('View'),
          },
          {
            id: 'projects:manage',
            name: this.translate.instant('Manage'),
            require: ['users:view', 'projects:view'],
          },
        ],
      },
      {
        name: this.translate.instant('Folder Templates'),
        icon: 'clone',
        component: 'folder_templates',
        items: [
          {
            id: 'folder_templates:manage',
            name: this.translate.instant('Manage'),
            require: ['projects:view'],
          },
        ],
      },
      {
        name: this.translate.instant('Users'),
        icon: 'users',
        items: [
          {
            id: 'users:view',
            name: this.translate.instant('View'),
          },
          {
            id: 'users:manage',
            name: this.translate.instant('Manage'),
            require: ['users:view'],
          },
        ],
      },
      {
        name: this.translate.instant('Workstations'),
        icon: 'network-wired',
        items: [
          {
            id: 'workstations:view',
            name: this.translate.instant('View'),
          },
          {
            id: 'workstations:manage',
            name: this.translate.instant('Manage'),
            require: ['workstations:view'],
          },
        ],
      },
      {
        name: this.translate.instant('Shares'),
        icon: 'share-alt',
        component: 'storage',
        items: [
          {
            id: 'shares:view',
            name: this.translate.instant('View'),
          },
          {
            id: 'shares:manage',
            name: this.translate.instant('Manage'),
            require: ['shares:view'],
          },
        ],
      },
      {
        name: this.translate.instant('File Manager'),
        icon: 'folders',
        component: 'storage',
        items: [
          {
            id: 'fs:fm',
            name: this.translate.instant('Access'),
          },
        ],
      },
      {
        name: this.translate.instant('Downloads'),
        icon: 'download',
        items: [
          {
            id: 'downloads:view',
            name: this.translate.instant('View'),
          },
        ],
      },
      {
        name: this.translate.instant('System'),
        icon: 'wrench',
        items: [
          {
            id: 'system:admin-access',
            name: this.translate.instant('Admin access'),
          },
        ],
      },
      {
        name: this.translate.instant('Automation'),
        icon: 'cog',
        component: 'automation',
        items: [
          {
            id: 'tasks:view',
            name: this.translate.instant('View'),
          },
          {
            id: 'tasks:start',
            name: this.translate.instant('Start jobs'),
            require: ['tasks:view'],
          },
          {
            id: 'tasks:manage',
            name: this.translate.instant('Manage'),
            require: ['tasks:view', 'tasks:start'],
          },
        ],
      },
      {
        name: this.translate.instant('Media Library'),
        icon: 'film',
        component: 'media',
        items: [
          {
            id: 'media:access',
            name: this.translate.instant('Access'),
          },
        ],
      },
      {
        name: this.translate.instant('Media Updates'),
        icon: 'comment',
        component: 'media',
        items: [
          {
            id: 'media:updates:manage',
            name: this.translate.instant('Manage'),
            require: ['media:access', 'users:view'],
          },
        ],
      },
      {
        name: this.translate.instant('Media Roots'),
        icon: 'folders',
        component: 'media',
        items: [
          {
            id: 'media:roots:manage',
            name: this.translate.instant('Manage'),
            require: ['media:access'],
          },
        ],
      },
      {
        name: this.translate.instant('Tape Library'),
        icon: 'tape',
        component: 'ltfs',
        items: [
          {
            id: 'ltfs:search',
            name: this.translate.instant('Search'),
          },
          {
            id: 'ltfs:backup',
            name: this.translate.instant('Archive'),
          },
          {
            id: 'ltfs:restore',
            name: this.translate.instant('Restore'),
          },
          {
            id: 'ltfs:manage',
            name: this.translate.instant('Manage'),
          },
        ],
      },
      {
        name: this.translate.instant('Tape Groups'),
        icon: 'th-large',
        component: 'ltfs',
        items: [
          {
            id: 'ltfs:tapegroups:manage',
            name: this.translate.instant('Manage'),
            require: ['ltfs:manage'],
          },
        ],
      },
    ]

    if (this.bootstrapData.cloudConnections.length > 0) {
      this.permissionSchema.permissionGroups.push({
        name: this.translate.instant('ELEMENTS Click'),
        icon: 'paper-plane',
        items: [
          {
            id: 'cloud:access',
            name: this.translate.instant('Access'),
            require: ['media:access'],
          },
        ],
      })
    }
  }

  loginErrorMessages = {
    InvalidPassword: this.translate.instant('Invalid username or password'),
    InvalidOTP: this.translate.instant('Invalid OTP'),
    UserDisabled: this.translate.instant('This user account is disabled or expired'),
    AmbiguousEmail: this.translate.instant('This e-mail adress belongs to more than one user'),
    RootLoginForbidden: this.translate.instant('Root login is forbidden on this node'),
    NoPassword: this.translate.instant('This user has no password'),
    WANLoginForbidden: this.translate.instant('WAN login forbidden'),
    PasswordChangeRequired: this.translate.instant('Password change required'),
  }
}

export function permissionGuard (permission: string): CanActivateFn {
  return () => inject(IdentityService).hasPermission(permission)
}
